import { BillingCode } from 'src/participantHistory/intakeNote/billingCodeConstants'
import { z } from 'zod'

export const CALL_STATUS_VALUES = [
  'CALL_HAD',
  'NO_SHOW',
  'LATE_CANCELLATION',
  'PROVIDER_ISSUE',
  'OTHER_EXCEPTION',
] as const

export const EXCEPTION_CALL_STATUS_VALUES = [
  'NO_SHOW',
  'LATE_CANCELLATION',
  'PROVIDER_ISSUE',
  'OTHER_EXCEPTION',
] as const

export type CallStatus = (typeof CALL_STATUS_VALUES)[number]
export type ExceptionCallStatus = (typeof EXCEPTION_CALL_STATUS_VALUES)[number]

/** Full clinical form for Call Had, and for legacy notes that predate CALL_STATUS. */
export const shouldShowFullCheckinCallForm = (callStatus: string | null | undefined) =>
  !callStatus || callStatus === 'CALL_HAD'

const metaSchema = z.object({
  date: z.string(),
  FIRST_NAME: z.string(),
  LAST_NAME: z.string(),
  MEMBER_ID: z.string(),
  PROVIDER_NAME: z.string(),
  LICENSE_TYPE: z.string(),
  CALL_START_TIME: z.string(),
  CALL_END_TIME: z.string().nullish(),
  SERVICE_DELIVERY_AND_COMPLIANCE: z.object({
    CONFIRM_SERVICE_PROVIDED_VIA_TELEHEALTH: z.boolean(),
    CONFIRM_SERVICE_PROVIDED_VIA_HIPAA_COMPLIANT_SOFTWARE: z.boolean(),
    CONFIRM_CALL_TIME_AND_LOCATION: z.boolean(),
  }),
})

export const callHadSchema = metaSchema.extend({
  CALL_STATUS: z.literal('CALL_HAD'),
  CALL_END_TIME: z.preprocess((val) => (val == null ? '' : val), z.string().min(1, 'Call end time is required')),
  CALL_METHOD: z.union([z.literal('IN_APP'), z.literal('PHONE_CALL')]),
  SMART_GOALS: z.string(),
  BILLING_CODES: z.array(z.nativeEnum(BillingCode)).min(1, 'At least one billing code is required'),
  REASON_FOR_CALL: z.string(),
  INTERVENTIONS: z.object({
    COGNITIVE_BEHAVIORAL_THERAPY_TECHNIQUES: z.boolean(),
    COGNITIVE_RESTRUCTURING: z.boolean(),
    COPING_SKILLS_DEVELOPMENT: z.boolean(),
    RELAPSE_PREVENTION_PLANNING: z.boolean(),
    MINDFULNESS_TECHNIQUES: z.boolean(),
    COMMUNICATION_SKILLS_TRAINING: z.boolean(),
    SOLUTION_FOCUSED_BRIEF_THERAPY_TECHNIQUES: z.boolean(),
    CRISIS_INTERVENTION: z.boolean(),
    MOTIVATIONAL_INTERVIEWING_TECHNIQUES: z.boolean(),
    RESOURCE_LINKAGE: z.boolean(),
    OTHER: z.string().optional(),
  }),
  RISK_LEVEL_HAS_CHANGE: z.enum(['yes', 'no']),
  PROTECTIVE_FACTORS_IDENTIFIED: z.object({
    STRONG_SOCIAL_SUPPORT: z.boolean(),
    ENGAGEMENT_IN_THERAPY: z.boolean(),
    COPING_SKILLS: z.boolean(),
    FUTURE_ORIENTED_THINKING: z.boolean(),
    MOTIVATED_FOR_TREATMENT: z.boolean(),
    RELIGIOUS_BELIEFS: z.boolean(),
    OTHER: z.string().optional(),
  }),
  NEXT_STEPS: z.object({
    SAFETY_PLAN_REVIEWED_EMAILED: z.boolean(),
    SAFETY_PLAN_DOCUMENTED_IN_RECORD: z.boolean(),
    NO_FURTHER_ASSISTANCE_NEEDED: z.boolean(),
    LAW_ENFORCEMENT_OR_EMERGENCY_CONTACT_NOTIFIED: z.boolean(),
    OTHER: z.string().optional(),
  }),
  PARTICIPANT_RESPONSE: z.object({
    ENGAGED_AND_SHOWED_UNDERSTANDING_OF_TECHNIQUES: z.boolean(),
    ACTIVE_IN_PROBLEM_SOLVING: z.boolean(),
    HIGHLIGHTED_PERSONAL_STRENGTH_AND_RESOURCES: z.boolean(),
    PRACTICED_AND_BUILT_CONFIDENCE: z.boolean(),
    EXPRESSED_MOTIVATION_TO_APPLY_TECHNIQUES: z.boolean(),
    OTHER: z.string().optional(),
  }),
  PLAN: z.object({
    CONTINUE_SUPPORT_VIA_APP_CHAT: z.boolean(),
    OFFER_CHECK_IN_CALLS_AS_NEEDED: z.boolean(),
    DISENROLL_PARTICIPANT_AT_REQUEST: z.boolean(),
    SEND_RESOURCES_AND_INFORMATION: z.boolean(),
    OTHER: z.string().optional(),
  }),
})

const descriptionField = {
  DESCRIPTION: z.string().trim().min(1, 'Description is required'),
}

const noShowSchema = metaSchema.extend({ CALL_STATUS: z.literal('NO_SHOW'), ...descriptionField })
const lateCancellationSchema = metaSchema.extend({
  CALL_STATUS: z.literal('LATE_CANCELLATION'),
  ...descriptionField,
})
const providerIssueSchema = metaSchema.extend({
  CALL_STATUS: z.literal('PROVIDER_ISSUE'),
  ...descriptionField,
})
const otherExceptionSchema = metaSchema.extend({
  CALL_STATUS: z.literal('OTHER_EXCEPTION'),
  ...descriptionField,
})

const isEndTimeOnOrAfterStartTime = (startTime: string, endTime: string) =>
  new Date(endTime).getTime() >= new Date(startTime).getTime()

export const CHECK_IN_CALL_NOTE_SCHEMA = z
  .discriminatedUnion('CALL_STATUS', [
    callHadSchema,
    noShowSchema,
    lateCancellationSchema,
    providerIssueSchema,
    otherExceptionSchema,
  ])
  .superRefine((data, ctx) => {
    if (!data.CALL_END_TIME || !data.CALL_START_TIME) {
      return
    }
    if (!isEndTimeOnOrAfterStartTime(data.CALL_START_TIME, data.CALL_END_TIME)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Call end time must be equal to or after call start time',
        path: ['CALL_END_TIME'],
      })
    }
  })

export type CheckinCallNoteType = Omit<z.infer<typeof callHadSchema>, 'CALL_STATUS' | 'DESCRIPTION'> & {
  CALL_STATUS: CallStatus
  DESCRIPTION?: string
}

export const CALL_STATUS_OPTIONS = [
  { value: 'CALL_HAD' as const, label: 'Call Had' },
  { value: 'NO_SHOW' as const, label: 'No Show' },
  { value: 'LATE_CANCELLATION' as const, label: 'Late Cancellation' },
  { value: 'PROVIDER_ISSUE' as const, label: 'Provider Issue' },
  { value: 'OTHER_EXCEPTION' as const, label: 'Other Exception' },
]
